import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import "./SendValidationPopup.css";
import { useSelector } from "react-redux";
import CustomAlertBox from "../CustomAlertBox/CustomAlertBox";

const SendValidationPopup = ({ open, onClose, record, onSend }) => {
  const [ahpraId, setAhpraId] = useState(record?.ahprA_ID || "");
  const [validationMessage, setValidationMessage] = useState("");
  const [isValidationEligible, setIsValidationEligible] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const user_mail = useSelector((state) => state.authentication.user.userEmail);
  const isEditable = !record?.ahprA_ID;

  // Helper function to check eligibility for validation
  const checkValidationEligibility = () => {
    const currentDate = new Date();
    const lastValidationDate = record?.date_last_AHPRA_validation
      ? new Date(record?.date_last_AHPRA_validation)
      : null;

    if (record?.aprhA_VALIDATION_STATUS === "Pending") {
      setValidationMessage("This record is already queued for validation.");
      setIsValidationEligible(false);
      return;
    }

    if (record?.aprhA_VALIDATION_STATUS === "Awaiting Approval") {
      setValidationMessage("This record is awaiting user approval after validation.");
      setIsValidationEligible(false);
      return;
    }

    if (
      record?.aprhA_VALIDATION_STATUS === "Validated" &&
      lastValidationDate &&
      (currentDate - lastValidationDate) / (1000 * 60 * 60 * 24 * 365) < 1
    ) {
      setValidationMessage(
        "This record was validated within the last year and is not eligible for validation."
      );
      setIsValidationEligible(false);
      return;
    }

    setValidationMessage("");
    setIsValidationEligible(true);
  };

  useEffect(() => {
    if (record) {
      checkValidationEligibility();
    }
  }, [record]);

  const formatDate = (date) => {
    return date
      ? new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(new Date(date))
      : "-"; 
  };

  const handleSend = () => {
    if (!ahpraId.trim()) {
      alert("A valid AHPRA ID is required!");
      return;
    }

    if (!isValidationEligible) {
      setIsAlertOpen(true);
      return;
    }

    const updatedRecord = {
      ...record,
      ahprA_ID: ahpraId,
    };

    onSend(updatedRecord, user_mail);
    onClose();
  };

  const handleAlertClose = () => {
    setIsAlertOpen(false);
  };

  const recordDetails = [
    { key: "OneKey ID", value: record?.inD_ID_CEGEDIM || "-" },
    {
      key: "AHPRA ID",
      value: (
        <TextField
          placeholder="Enter AHPRA ID"
          value={ahpraId}
          onChange={(e) => setAhpraId(e.target.value)}
          className="input-field"
          variant="outlined"
          size="small"
          disabled={!isEditable} 
        />
      ),
    },
    { key: "First Name", value: record?.firstname || "-" },
    { key: "Middle Name", value: record?.middlename || "-" },
    { key: "Last Name", value: record?.lastname || "-" },
    { key: "Administrative Name", value: record?.administrativE_NAME || "-" },
    { key: "Nickname", value: record?.nickname || "-" },
    { key: "Type", value: record?.inD_TYPE_lbl || "-" },
    { key: "Specialty 1", value: record?.specialtY_1_lbl || "-" },
    { key: "Specialty 2", value: record?.specialtY_2_lbl || "-" },
    { key: "Specialty 3", value: record?.specialtY_3_lbl || "-" },
    { key: "State", value: record?.hcP_State || "-" },
    { key: "City", value: record?.hcP_City || "-" },
    { key: "Post Code", value: record?.hcP_PostCode || "-" },
    {
      key: "Last AHPRA Validation Date",
      value: formatDate(record?.date_last_AHPRA_validation),
    },
    { key: "OK User", value: record?.ok_user || "-" },
    { key: "OK Comment", value: record?.oK_Comment || "-" },
    {
      key: "Last Validation Date",
      value: formatDate(record?.aprhA_LAST_VALIDATION_DATE),
    },
    {
      key: "Validation Status",
      value: record?.aprhA_VALIDATION_STATUS || "-",
    },
  ];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <Box className="popup-container">
          <DialogTitle
            className="popup-header"
            sx={{
              textAlign: "center",
              fontSize: "1.5rem",
              fontWeight: "bold",
              color: "white",
              backgroundColor: "#2E3B55",
            }}
          >
            Record Details
          </DialogTitle>

          <DialogContent className="popup-content">
            <Box className="details-section">
              {recordDetails.map(({ key, value }, index) => (
                <Box key={index} className="detail-item">
                  <Typography className="detail-key">{key} :</Typography>
                  <Typography className="detail-value">{value}</Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>

          <DialogActions
            className="popup-footer"
            sx={{ padding: "1rem", backgroundColor: "#f5f5f5" }}
          >
            <Button
              onClick={onClose}
              variant="outlined"
              className="close-button"
              sx={{
                border: "1px solid #2E3B55",
                color: "#2E3B55",
                "&:hover": { backgroundColor: "#e0e0e0" },
              }}
            >
              Close
            </Button>
            <Button
              onClick={handleSend}
              variant="contained"
              className="send-button"
              sx={{
                backgroundColor: "#2E3B55",
                color: "#fff",
                "&:hover": { backgroundColor: "#1d2a3f" },
              }}
            >
              Send for AHPRA Validation
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <CustomAlertBox
        open={isAlertOpen}
        onClose={handleAlertClose}
        message={validationMessage}
      />
    </>
  );
};

export default SendValidationPopup;
